// Cost displays that never hide a discount. A player's implemented fortresses
// can lower what they pay — Inverse Molehill discounts a whole fortress deck,
// Dungeon Dice Martyr's hand cards cost 1 Obsidian less, Resource Sequencer
// makes mines cheaper, Hippoman cuts every Food cost — and a bare number would
// silently disagree with the printed cost everyone else pays. `ResourceCost`
// keeps the printed figure struck through with the discounted one beside it
// ("3̶ 1"); with nothing to discount it renders the single number it always
// did.
//
// A fortress card's price depends on the viewer, so `ObsidianCostProvider`
// carries their lookup down to every display of it (market tiles, watchlist
// rows, card faces, implement options). The catalogue/editor pages render
// without a provider and simply show printed prices.

import { createContext, useContext } from "react";
import type { FortressCard } from "@fortress/shared";
import { ResourceIcon, type ResourceKind } from "./resources";

/** Card → the Obsidian the viewer would actually pay for it. */
export type ObsidianCostLookup = (
  card: Pick<FortressCard, "id" | "kind" | "cost">,
) => number;

const printedCost: ObsidianCostLookup = (card) => card.cost[3] ?? 0;

const ObsidianCostContext = createContext<ObsidianCostLookup>(printedCost);

/** Supplies the viewer's discounted fortress prices to every cost display
 * below it (market tiles, watchlist rows, card faces, implement options). */
export function ObsidianCostProvider({
  value,
  children,
}: {
  value: ObsidianCostLookup;
  children: React.ReactNode;
}) {
  return (
    <ObsidianCostContext.Provider value={value}>
      {children}
    </ObsidianCostContext.Provider>
  );
}

/** The viewer's fortress-price lookup — the printed price outside a game. */
export function useObsidianCost(): ObsidianCostLookup {
  return useContext(ObsidianCostContext);
}

const PlayerCountContext = createContext<number | undefined>(undefined);

/** Supplies the match's seated-player count to the card faces below it, for
 * the "per player" figures some cards print — Horny Neighbour's "50/X" price,
 * Big Sprawler's "72/X tiles". Outside a game there is none. */
export function PlayerCountProvider({
  value,
  children,
}: {
  value: number | undefined;
  children: React.ReactNode;
}) {
  return (
    <PlayerCountContext.Provider value={value}>{children}</PlayerCountContext.Provider>
  );
}

/** The match's seated-player count, or undefined outside a game. */
export function usePlayerCount(): number | undefined {
  return useContext(PlayerCountContext);
}

/** Card prose "N/X" figures: with a seat count known, the value this match
 * uses follows in brackets — "72/X (18)". Outside a game the figure stands. */
export function withPerPlayerFigures(text: string, playerCount: number | undefined): string {
  if (!playerCount) return text;
  return text.replace(
    /\b(\d+)\/X\b/g,
    (figure, n: string) => `${figure} (${Math.ceil(Number(n) / playerCount)})`,
  );
}

/** Resource icon + price: the printed price alone, or — when the viewer pays
 * less — the printed price struck through with the real price to its right. */
export function ResourceCost({
  kind,
  printed,
  paid,
  size = 14,
}: {
  kind: ResourceKind;
  printed: number;
  /** What the viewer pays; defaults to (and never exceeds) `printed`. */
  paid?: number;
  size?: number;
}) {
  const real = paid ?? printed;
  // One element for the whole price: the hosts lay their cost row out with
  // `gap`, and a bare text node between the icon and the numbers would become
  // a flex item of its own (a gap on either side of a lone space).
  return (
    <>
      <ResourceIcon kind={kind} size={size} />
      <span className="cost-value">
        <DiscountedNumber printed={printed} paid={real} />
      </span>
    </>
  );
}

/** The bare figures — printed struck through, then what's actually paid — for
 * costs that read as prose rather than an icon + number ("Pay 3̶ 2 Food"). */
export function DiscountedNumber({
  printed,
  paid,
}: {
  printed: number;
  paid: number;
}) {
  return (
    <>
      {paid < printed && <s className="cost-printed">{printed}</s>}
      {paid}
    </>
  );
}
